import { motion } from "framer-motion";

/**
 * Pure SVG/CSS abstract visualization representing AI + data integration.
 * - Rotating dashed orbits
 * - Pulsing node constellation
 * - Central core with soft indigo glow
 */
export default function HeroVisual() {
  const nodes = [
    { x: 50, y: 12, d: 0 },
    { x: 88, y: 32, d: 0.4 },
    { x: 92, y: 70, d: 0.8 },
    { x: 60, y: 92, d: 1.2 },
    { x: 18, y: 82, d: 1.6 },
    { x: 8, y: 44, d: 2.0 },
    { x: 30, y: 22, d: 2.4 },
    { x: 70, y: 50, d: 0.2 },
  ];
  const cx = 50;
  const cy = 50;

  return (
    <div data-testid="hero-visual" className="absolute inset-0 flex items-center justify-center">
      {/* Outer rotating ring */}
      <div className="absolute w-[88%] aspect-square rounded-full border border-indigo-500/15 animate-spin-slow" />
      <div
        className="absolute w-[68%] aspect-square rounded-full border border-dashed border-indigo-400/25 animate-spin-slow"
        style={{ animationDuration: "45s", animationDirection: "reverse" }}
      />
      <div className="absolute w-[44%] aspect-square rounded-full border border-indigo-300/20 animate-spin-slow" />

      {/* Central glowing orb */}
      <div className="relative">
        <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-indigo-400 via-indigo-500 to-indigo-700 glow-indigo animate-pulse-glow" />
        <div className="absolute inset-2 rounded-full bg-[#0B1120]/60 backdrop-blur-md border border-white/10 flex items-center justify-center">
          <div className="font-display text-2xl md:text-3xl text-white tracking-tight">
            AI<span className="text-indigo-300">×</span>HR
          </div>
        </div>
      </div>

      {/* Constellation overlay */}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="dot" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a5b4fc" stopOpacity="1" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="line" x1="0" x2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.0" />
            <stop offset="50%" stopColor="#818cf8" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
          </linearGradient>
        </defs>

        {/* Connection lines from center */}
        {nodes.map((n, i) => (
          <motion.line
            key={`l-${i}`}
            x1={cx}
            y1={cy}
            x2={n.x}
            y2={n.y}
            stroke="url(#line)"
            strokeWidth="0.25"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.4 + n.d * 0.15 }}
          />
        ))}

        {/* Outer nodes */}
        {nodes.map((n, i) => (
          <g key={`n-${i}`}>
            <motion.circle
              cx={n.x}
              cy={n.y}
              r="0.9"
              fill="#a5b4fc"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 + n.d * 0.2, duration: 0.6 }}
            />
            <motion.circle
              cx={n.x}
              cy={n.y}
              r="3"
              fill="url(#dot)"
              initial={{ opacity: 0 }}
              animate={{ opacity: [0.2, 0.8, 0.2] }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: n.d,
                ease: "easeInOut",
              }}
            />
          </g>
        ))}
      </svg>

      {/* Floating particles */}
      <div className="absolute top-8 right-10 w-2 h-2 rounded-full bg-indigo-300 shadow-[0_0_18px_rgba(165,180,252,0.9)] animate-pulse" />
      <div className="absolute bottom-12 left-8 w-1.5 h-1.5 rounded-full bg-indigo-200 shadow-[0_0_14px_rgba(199,210,254,0.9)] animate-pulse" style={{ animationDelay: "1.5s" }} />
    </div>
  );
}
